import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { Empty, Tooltip, Tag } from 'antd';
import {
  DoubleLeftOutlined,
  DoubleRightOutlined,
  LeftOutlined,
  RightOutlined,
  CaretRightOutlined,
  CaretDownOutlined
} from '@ant-design/icons';
import {
  useReactTable,
  getCoreRowModel,
  flexRender
} from '@tanstack/react-table';
import Spinner from '../../CustomComponents/Spinner';
import * as Styled from '../JobList/TanStackTableStyles';

const formatBody = body => {
  if (!body) return null;
  try {
    return JSON.stringify(JSON.parse(body), null, 2);
  } catch {
    return body;
  }
};

const DetailSection = ({ title, children }) => (
  <div style={{ marginBottom: 12 }}>
    <div
      style={{
        fontWeight: 600,
        fontSize: 12,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: '#888',
        marginBottom: 6
      }}
    >
      {title}
    </div>
    {children}
  </div>
);

const DetailField = ({ label, value }) => {
  if (!value && value !== 0) return null;
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 4, fontSize: 13 }}>
      <span style={{ color: '#888', minWidth: 120, flexShrink: 0 }}>
        {label}:
      </span>
      <span style={{ wordBreak: 'break-all' }}>{value}</span>
    </div>
  );
};

const CodeBlock = ({ label, content }) => {
  if (!content) return null;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>
        {label}:
      </div>
      <pre
        style={{
          background: '#1e1e1e',
          color: '#d4d4d4',
          padding: '10px 14px',
          borderRadius: 6,
          fontSize: 12,
          overflowX: 'auto',
          maxHeight: 300,
          margin: 0,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all'
        }}
      >
        {content}
      </pre>
    </div>
  );
};

const AccordionDetail = ({ row: rowData }) => {
  const {
    request,
    response,
    source,
    direction,
    userType,
    kafkaContext,
    auditError
  } = rowData;
  const method = request?.method;
  const hasRequestBody = method !== 'GET' && request?.body;

  return (
    <div
      style={{
        padding: '16px 24px',
        background: '#fafafa',
        borderTop: '1px solid #f0f0f0'
      }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {/* Request */}
        <DetailSection title='Request'>
          <DetailField label='Content-Type' value={request?.contentType} />
          <DetailField label='Remote Address' value={request?.remoteAddress} />
          <DetailField label='User Agent' value={request?.userAgent} />
          {hasRequestBody && (
            <CodeBlock label='Body' content={formatBody(request.body)} />
          )}
        </DetailSection>

        {/* Response */}
        <DetailSection title='Response'>
          <DetailField label='Content-Type' value={response?.contentType} />
          {response?.body && (
            <CodeBlock label='Body' content={formatBody(response.body)} />
          )}
        </DetailSection>
      </div>

      {/* Extra info row */}
      {(direction || userType || kafkaContext || auditError) && (
        <div
          style={{
            display: 'flex',
            gap: 32,
            flexWrap: 'wrap',
            marginTop: 8,
            paddingTop: 12,
            borderTop: '1px solid #f0f0f0'
          }}
        >
          {direction && (
            <div style={{ fontSize: 13 }}>
              <span style={{ color: '#888' }}>Direction: </span>
              <Tag>{direction}</Tag>
            </div>
          )}
          {userType && (
            <div style={{ fontSize: 13 }}>
              <span style={{ color: '#888' }}>User Type: </span>
              <span>{userType}</span>
            </div>
          )}
          {kafkaContext && (
            <div style={{ fontSize: 13 }}>
              <span style={{ color: '#888' }}>Kafka: </span>
              <span>{kafkaContext.messageType}</span>
              {kafkaContext.facilityId && (
                <span style={{ color: '#aaa', marginLeft: 6 }}>
                  ({kafkaContext.facilityId})
                </span>
              )}
            </div>
          )}
          {auditError && (
            <div style={{ fontSize: 13 }}>
              <span style={{ color: '#888' }}>Error: </span>
              <span style={{ color: 'red' }}>{auditError}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const TanStackAuditTable = ({
  data,
  columns: columnDefs,
  loading,
  total,
  pageNo,
  pageSize,
  pageNoChangeHandler
}) => {
  const [theme] = useSelector(state => [state.themeReducer]);
  const [columnSizing, setColumnSizing] = useState({});
  const [expandedRows, setExpandedRows] = useState(new Set());

  const totalPages = Math.ceil(total / pageSize) || 1;

  const goToPage = page => {
    setExpandedRows(new Set());
    pageNoChangeHandler(page);
  };

  const table = useReactTable({
    data: data || [],
    columns: columnDefs,

    // Server-side pagination (we manage page externally)
    manualPagination: true,
    pageCount: totalPages,

    // Column features
    enableColumnResizing: true,
    columnResizeMode: 'onChange',

    // State
    state: {
      columnSizing
    },

    // Callbacks
    onColumnSizingChange: setColumnSizing,

    // Core model
    getCoreRowModel: getCoreRowModel(),

    getRowId: (_, index) => String(index)
  });

  const isEmpty = !data || data.length === 0;
  const startItem = pageNo * pageSize + 1;
  const endItem = Math.min((pageNo + 1) * pageSize, total);

  return (
    <ThemeProvider theme={theme}>
      <Styled.TableContainer style={{ position: 'relative' }}>
        {/* Loading overlay */}
        {loading && (
          <Styled.LoadingOverlay>
            <Spinner />
          </Styled.LoadingOverlay>
        )}

        {/* Empty state */}
        {isEmpty && !loading ? (
          <Styled.EmptyState>
            <Empty description='No audit logs found' />
          </Styled.EmptyState>
        ) : (
          <>
            {/* Table */}
            <div
              style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 310px)' }}
            >
              <Styled.StyledTable role='table' aria-label='Audit logs table'>
                {/* Table Head */}
                <Styled.StyledTableHead role='rowgroup'>
                  {table.getHeaderGroups().map(headerGroup => (
                    <Styled.StyledTableHeaderRow
                      key={headerGroup.id}
                      role='row'
                    >
                      {headerGroup.headers.map(header => (
                        <Styled.StyledTableHeader
                          key={header.id}
                          role='columnheader'
                          style={{ width: header.getSize() }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}

                          {/* Resize handle */}
                          {header.column.getCanResize() && (
                            <Styled.ResizeHandle
                              onMouseDown={header.getResizeHandler()}
                              onTouchStart={header.getResizeHandler()}
                              isResizing={header.column.getIsResizing()}
                              onClick={e => e.stopPropagation()}
                            />
                          )}
                        </Styled.StyledTableHeader>
                      ))}
                    </Styled.StyledTableHeaderRow>
                  ))}
                </Styled.StyledTableHead>

                {/* Table Body */}
                <Styled.StyledTableBody role='rowgroup'>
                  {table.getRowModel().rows.map(row => {
                    const isExpanded = expandedRows.has(row.id);
                    const colCount = row.getVisibleCells().length;
                    return (
                      <React.Fragment key={row.id}>
                        <Styled.StyledTableRow
                          role='row'
                          expandable
                          style={{ cursor: 'pointer' }}
                          onClick={() =>
                            setExpandedRows(prev => {
                              const next = new Set(prev);
                              isExpanded
                                ? next.delete(row.id)
                                : next.add(row.id);
                              return next;
                            })
                          }
                        >
                          {row.getVisibleCells().map((cell, idx) => (
                            <Styled.StyledTableCell key={cell.id} role='cell'>
                              {idx === 0 && (
                                <span
                                  style={{
                                    marginRight: 6,
                                    color: '#47a4ea',
                                    fontSize: 14,
                                    transition: 'transform 0.15s'
                                  }}
                                >
                                  {isExpanded ? (
                                    <CaretDownOutlined />
                                  ) : (
                                    <CaretRightOutlined />
                                  )}
                                </span>
                              )}
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </Styled.StyledTableCell>
                          ))}
                        </Styled.StyledTableRow>
                        {isExpanded && (
                          <tr role='row'>
                            <td
                              colSpan={colCount}
                              style={{ padding: 0, border: 'none' }}
                            >
                              <AccordionDetail row={row.original} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </Styled.StyledTableBody>
              </Styled.StyledTable>
            </div>

            {/* Pagination Controls */}
            <Styled.PaginationContainer>
              <Styled.PaginationInfo>
                {total > 0
                  ? `Showing ${startItem} to ${endItem} of ${total} results`
                  : 'No results'}
              </Styled.PaginationInfo>

              <Styled.PaginationControls>
                <Tooltip title='First Page'>
                  <Styled.PaginationButton
                    type='text'
                    icon={<DoubleLeftOutlined />}
                    disabled={pageNo === 0}
                    onClick={() => goToPage(0)}
                    aria-label='First Page'
                  />
                </Tooltip>

                <Tooltip title='Previous Page'>
                  <Styled.PaginationButton
                    type='text'
                    icon={<LeftOutlined />}
                    disabled={pageNo === 0}
                    onClick={() => goToPage(pageNo - 1)}
                    aria-label='Previous Page'
                  />
                </Tooltip>

                <span>
                  Page {pageNo + 1} of {totalPages}
                </span>

                <Tooltip title='Next Page'>
                  <Styled.PaginationButton
                    type='text'
                    icon={<RightOutlined />}
                    disabled={pageNo + 1 >= totalPages}
                    onClick={() => goToPage(pageNo + 1)}
                    aria-label='Next Page'
                  />
                </Tooltip>

                <Tooltip title='Last Page'>
                  <Styled.PaginationButton
                    type='text'
                    icon={<DoubleRightOutlined />}
                    disabled={pageNo + 1 >= totalPages}
                    onClick={() => goToPage(totalPages - 1)}
                    aria-label='Last Page'
                  />
                </Tooltip>
              </Styled.PaginationControls>
            </Styled.PaginationContainer>
          </>
        )}
      </Styled.TableContainer>
    </ThemeProvider>
  );
};

export default TanStackAuditTable;
